import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit, Trash2, Eye, EyeOff, Search } from 'lucide-react';
import './AdminNews.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminNews = () => {
  const { token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await axios.get(`${API}/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(response.data);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(news.filter(n => n.id !== id));
      setShowDeleteModal(false);
      setDeleteId(null);
    } catch (error) {
      console.error('Erro ao eliminar notícia:', error);
    }
  };

  const togglePublish = async (newsItem) => {
    try {
      await axios.put(`${API}/news/${newsItem.id}`, {
        ...newsItem,
        published: !newsItem.published
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadNews();
    } catch (error) {
      console.error('Erro ao atualizar notícia:', error);
    }
  };

  const filteredNews = news.filter(n => 
    n.title.toLowerCase().includes(search.toLowerCase()) ||
    n.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="loading">A carregar...</div>;
  }

  return (
    <div className="admin-news">
      <div className="news-header">
        <div>
          <h1>Gestão de Notícias</h1>
          <p>{news.length} notícias no total</p>
        </div>
        <Link to="/admin/dashboard/news/new" className="add-news-btn">
          <Plus size={20} />
          Nova Notícia
        </Link>
      </div>

      <div className="news-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Pesquisar notícias..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="news-list">
        {filteredNews.length === 0 ? (
          <div className="empty-state">
            <p>Não foram encontradas notícias</p>
            <Link to="/admin/dashboard/news/new" className="create-btn">
              Criar primeira notícia
            </Link>
          </div>
        ) : (
          <table className="news-table">
            <thead>
              <tr>
                <th>Imagem</th>
                <th>Título</th>
                <th>Estado</th>
                <th>Data</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredNews.map(item => (
                <tr key={item.id}>
                  <td className="thumb-cell">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="news-thumb" />
                    ) : (
                      <div className="no-thumb">Sem imagem</div>
                    )}
                  </td>
                  <td className="title-cell">
                    <strong>{item.title}</strong>
                    <p>{item.description?.substring(0, 100)}...</p>
                  </td>
                  <td>
                    <span className={`status-badge ${item.published ? 'published' : 'draft'}`}>
                      {item.published ? 'Publicada' : 'Rascunho'}
                    </span>
                  </td>
                  <td className="date-cell">
                    {new Date(item.created_at).toLocaleDateString('pt-PT')}
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn toggle"
                      onClick={() => togglePublish(item)}
                      title={item.published ? 'Despublicar' : 'Publicar'}
                    >
                      {item.published ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <Link 
                      to={`/admin/dashboard/news/edit/${item.id}`}
                      className="action-btn edit"
                      title="Editar"
                    >
                      <Edit size={16} />
                    </Link>
                    <button 
                      className="action-btn delete"
                      onClick={() => {
                        setDeleteId(item.id);
                        setShowDeleteModal(true);
                      }}
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Confirmar eliminação</h3>
            <p>Tem a certeza que deseja eliminar esta notícia? Esta ação não pode ser revertida.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </button>
              <button className="delete-btn" onClick={() => handleDelete(deleteId)}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
